/**
 * Print options dialog: choose the label stock, see the labels at true size with any size warnings, print.
 *
 * Printing happens in the dialog's own Print click, so the browser's print call is always inside a
 * user gesture. The chosen stock becomes this device's stock for Print.
 */

const libPictView = require('pict-view');
const libMatrix = require('../rendering/QR-Matrix.js');

const DIALOG_ID = 'HeadlightQR-PrintDialog';

const _DefaultConfiguration =
{
	ViewIdentifier: 'HeadlightQR-PrintDialog',
	AutoRender: false,
	CSS: /*css*/`
.hlqr-print-backdrop { position: fixed; inset: 0; z-index: 1180; display: flex; align-items: center; justify-content: center; background: rgba(15, 23, 42, 0.55); }
.hlqr-print-card { width: min(94vw, 760px); max-height: 92vh; display: flex; flex-direction: column; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1f2933); border-radius: 10px; box-shadow: 0 10px 40px rgba(0,0,0,0.4); }
.hlqr-print-head { display: flex; flex-wrap: wrap; gap: 0.6rem 1rem; align-items: center; padding: 0.9rem 1.1rem; border-bottom: 1px solid var(--theme-color-border-default, #d5dbe1); }
.hlqr-print-head h2 { font-size: 1.05rem; margin: 0; flex: 1 1 auto; }
.hlqr-print-head label { font-size: 0.85rem; display: inline-flex; gap: 0.4rem; align-items: center; }
.hlqr-print-head label[hidden] { display: none; }
.hlqr-print-head select, .hlqr-print-head input { padding: 0.3rem 0.45rem; border: 1px solid var(--theme-color-border-default, #c5ccd3); border-radius: 6px; background: var(--theme-color-background-panel, #fff); color: inherit; }
.hlqr-print-plan { padding: 0.5rem 1.1rem 0; font-size: 0.85rem; opacity: 0.85; }
.hlqr-print-warning { padding: 0.25rem 1.1rem 0; font-size: 0.85rem; color: #b91c1c; }
.hlqr-print-preview { flex: 1 1 auto; overflow: auto; padding: 1rem 1.1rem; display: flex; flex-direction: column; gap: 0.8rem; align-items: flex-start; background: repeating-linear-gradient(45deg, rgba(0,0,0,0.04), rgba(0,0,0,0.04) 10px, transparent 10px, transparent 20px); }
.hlqr-print-preview .hlqr-print-page { box-shadow: 0 1px 4px rgba(0,0,0,0.25); }
.hlqr-print-foot { display: flex; justify-content: flex-end; gap: 0.5rem; padding: 0.8rem 1.1rem; border-top: 1px solid var(--theme-color-border-default, #d5dbe1); }
.hlqr-print-button { border: 1px solid var(--theme-color-border-default, #c5ccd3); background: transparent; color: inherit; border-radius: 6px; padding: 0.45rem 1rem; cursor: pointer; }
.hlqr-print-button--primary { background: #1d4ed8; border-color: #1d4ed8; color: #fff; font-weight: 600; }
`,
};

class HeadlightQRPrintDialog extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DefaultConfiguration, pOptions), pServiceHash);
		/** @type {any} */
		this.pict;
		this.items = [];
		this.presetID = '';
		this.startIndex = 0;
		this.title = '';
	}

	/**
	 * @param {Array<{ Entity?: string, Value?: string, Payload?: string, Lines?: Array<string> }>} pItems
	 * @param {Object} [pOptions] - `{ Title }`
	 */
	open(pItems, pOptions)
	{
		const tmpOptions = pOptions || {};
		const tmpProvider = this.pict.providers.HeadlightQR;
		this.items = pItems || [];
		this.presetID = tmpProvider.getDeviceMediaPreset().Id;
		this.startIndex = 0;
		this.title = tmpOptions.Title || ((this.items.length === 1) ? 'Print label' : `Print ${this.items.length} labels`);
		this.close();
		const tmpBackdrop = document.createElement('div');
		tmpBackdrop.id = DIALOG_ID;
		tmpBackdrop.className = 'hlqr-print-backdrop';
		tmpBackdrop.setAttribute('role', 'dialog');
		tmpBackdrop.setAttribute('aria-modal', 'true');
		const tmpDefaultID = tmpProvider.getDefaultMediaPreset().Id;
		const tmpPresets = tmpProvider.getMediaPresets().map((pPreset) => `<option value="${libMatrix.escapeXML(pPreset.Id)}"${pPreset.Id === this.presetID ? ' selected' : ''}>${libMatrix.escapeXML(pPreset.Name)}${pPreset.Id === tmpDefaultID ? ' (default)' : ''}</option>`).join('');
		tmpBackdrop.innerHTML = `<div class="hlqr-print-card">`
			+ `<div class="hlqr-print-head"><h2>${libMatrix.escapeXML(this.title)}</h2>`
			+ `<label>Label stock <select data-hlqr-print="preset">${tmpPresets}</select></label>`
			+ `<label data-hlqr-print="start-wrap">Start at label <input type="number" min="1" value="1" style="width:4.5rem" data-hlqr-print="start"></label></div>`
			+ `<div class="hlqr-print-plan" data-hlqr-print="plan"></div><div data-hlqr-print="warnings"></div>`
			+ `<div class="hlqr-print-preview" data-hlqr-print="preview"></div>`
			+ `<div class="hlqr-print-foot"><button type="button" class="hlqr-print-button" data-hlqr-print="cancel">Cancel</button>`
			+ `<button type="button" class="hlqr-print-button hlqr-print-button--primary" data-hlqr-print="print">Print</button></div></div>`;
		document.body.appendChild(tmpBackdrop);
		tmpBackdrop.addEventListener('change', (pEvent) => this.handleChange(pEvent));
		tmpBackdrop.addEventListener('click', (pEvent) => this.handleClick(pEvent, tmpBackdrop));
		this.refresh();
	}

	/**
	 * @param {Event} pEvent
	 */
	handleChange(pEvent)
	{
		const tmpTarget = /** @type {HTMLInputElement} */ (pEvent.target);
		const tmpRole = tmpTarget.getAttribute('data-hlqr-print');
		if (tmpRole === 'preset')
		{
			this.presetID = tmpTarget.value;
		}
		if (tmpRole === 'start')
		{
			this.startIndex = Math.max(0, (parseInt(tmpTarget.value, 10) || 1) - 1);
		}
		this.refresh();
	}

	/**
	 * @param {MouseEvent} pEvent
	 * @param {HTMLElement} pBackdrop
	 */
	handleClick(pEvent, pBackdrop)
	{
		const tmpTarget = /** @type {HTMLElement} */ (pEvent.target);
		const tmpRole = tmpTarget.getAttribute('data-hlqr-print');
		if (tmpTarget === pBackdrop || tmpRole === 'cancel')
		{
			this.close();
			return;
		}
		if (tmpRole === 'print')
		{
			const tmpProvider = this.pict.providers.HeadlightQR;
			// The stock picked here is what Print uses on this device; picking the default follows the customer's default again.
			if (this.presetID === tmpProvider.getDefaultMediaPreset().Id)
			{
				tmpProvider.clearDeviceMediaPreset();
			}
			else
			{
				tmpProvider.setDeviceMediaPreset(this.presetID);
			}
			tmpProvider.printLabels(this.items, { PresetID: this.presetID, StartIndex: this.startIndex });
			this.close();
		}
	}

	refresh()
	{
		const tmpDialog = document.getElementById(DIALOG_ID);
		if (!tmpDialog)
		{
			return;
		}
		const tmpProvider = this.pict.providers.HeadlightQR;
		try
		{
			const tmpJob = tmpProvider.previewLabels(this.items, { PresetID: this.presetID, StartIndex: this.startIndex });
			const tmpPlan = tmpJob.Plans[0];
			const tmpStartWrap = tmpDialog.querySelector('[data-hlqr-print="start-wrap"]');
			tmpStartWrap.hidden = (tmpJob.Preset.Kind !== 'Sheet');
			tmpDialog.querySelector('[data-hlqr-print="plan"]').textContent = tmpPlan ? `Each QR square prints at ${tmpPlan.ModuleMM.toFixed(2)} mm; the code is ${tmpPlan.CodeMM.toFixed(1)} mm wide.` : '';
			const tmpWarnings = Array.from(new Set(tmpJob.Plans.flatMap((pPlan) => pPlan.Warnings.map((pWarning) => pWarning.Message))));
			tmpDialog.querySelector('[data-hlqr-print="warnings"]').innerHTML = tmpWarnings.map((pMessage) => `<div class="hlqr-print-warning">${libMatrix.escapeXML(pMessage)}</div>`).join('');
			tmpDialog.querySelector('[data-hlqr-print="preview"]').innerHTML = tmpJob.HTML;
		}
		catch (pError)
		{
			tmpDialog.querySelector('[data-hlqr-print="warnings"]').innerHTML = `<div class="hlqr-print-warning">${libMatrix.escapeXML(pError.message || pError)}</div>`;
		}
	}

	close()
	{
		const tmpExisting = (typeof document !== 'undefined') ? document.getElementById(DIALOG_ID) : null;
		if (tmpExisting)
		{
			tmpExisting.remove();
		}
	}
}

module.exports = HeadlightQRPrintDialog;
module.exports.default_configuration = _DefaultConfiguration;
